<template>
  <div class="checkbox-groups filter">
    <div
      v-for="(group) in groups"
      :key="group.value"
      class="group"
    >
      <div class="group-header">
        <checkbox-item
          :label="capitalize(group.name.toLowerCase())"
          :checked="isGroupChecked(group)"
          :indeterminate="isGroupIndeterminate(group)"
          @change="() => toggleGroup(group)"
        />

        <button
          v-if="group.colors && group.colors.length"
          class="expand-button"
          @click="group.expanded = !group.expanded"
        >
          <feather-icon
            :icon="getExpandIconName(group)"
            size="16"
          />
        </button>
      </div>

      <div
        v-if="group.expanded"
        class="nested-options"
      >
        <checkbox-item
          v-for="color in group.colors"
          :key="color.value"
          :label="color.name"
          :checked="isColorSelected(color.value)"
          @change="onColorToggle(group, color)"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { capitalize } from '@core/utils/filter'
import {
  getExpandIconName,
  isColorSelected,
  isGroupChecked,
  isGroupIndeterminate,
} from '@/views/apps/retailer-ai/utils'
import CheckboxItem from './CheckboxItem.vue'

export default {
  name: 'NestedCheckboxGroup',
  components: { CheckboxItem },
  props: {
    groups: {
      type: Array,
      required: true,
    },
    selectedValues: {
      type: Object,
      default: () => ({}),
    },
  },
  methods: {
    capitalize,
    getExpandIconName,

    isColorSelected(colorValue) {
      return isColorSelected(colorValue, this.selectedValues)
    },

    isGroupChecked(group) {
      return isGroupChecked(group, this.selectedValues)
    },

    isGroupIndeterminate(group) {
      return isGroupIndeterminate(group, this.selectedValues)
    },

    toggleGroup(group) {
      const updated = this.cloneSelectedValues()
      const isActive = updated.color_family_ids.includes(group.id)

      if (isActive) {
        updated.color_family_ids = updated.color_family_ids.filter(id => id !== group.id)
        updated.colors = updated.colors.filter(
          c => !group.colors.some(col => col.value === c),
        )
      } else {
        updated.color_family_ids.push(group.id)
        const groupColorValues = group.colors.map(c => c.value)
        updated.colors = updated.colors.filter(c => !groupColorValues.includes(c))
      }

      this.$emit('change', updated)
    },

    onColorToggle(group, color) {
      const updated = this.cloneSelectedValues()

      if (this.isColorSelected(color.value)) {
        this.removeColor(updated, group, color)
      } else {
        this.addColor(updated, group, color)
      }

      this.$emit('change', updated)
    },

    cloneSelectedValues() {
      return {
        ...this.selectedValues,
        colors: [...(this.selectedValues.colors || [])],
        color_family_ids: [...(this.selectedValues.color_family_ids || [])],
      }
    },

    addColor(updated, group, color) {
      updated.colors.push(color.value)

      if (!updated.color_family_ids.includes(group.id)) {
        updated.color_family_ids.push(group.id)
      }
    },

    removeColor(updated, group, color) {
      updated.colors = updated.colors.filter(c => c !== color.value)

      const otherColorsExist = group.colors.some(
        c => c.value !== color.value && updated.colors.includes(c.value),
      )

      if (!otherColorsExist) {
        updated.color_family_ids = updated.color_family_ids.filter(id => id !== group.id)
      }
    },
  },
}
</script>

<style scoped>
.checkbox-groups {
  display: flex;
  flex-direction: column;
}

.filter {
  font-size: 14px;
  line-height: 21px;
  width: 100%;
  margin-bottom: 16px;
}

.group-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.nested-options {
  margin-left: 20px;
  margin-top: 8px;
  display: flex;
  flex-direction: column;
}

.expand-button {
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 4px;
}

.expand-button:hover {
  background-color: rgba(0, 0, 0, 0.05);
}
</style>
